import type { PrV2CommentResponse, PrV2ReviewResponse, PrV2ReviewThread } from '@/lib/pr-v2/types';

import type { PrV2LocalScopeState } from './types';

import { getPrV2CommentTransitionKey } from '@/lib/pr-v2/comment-lifecycle';
import { getStableViewKey } from '@/lib/pr-v2/stable-view-keys';
import { asRecord } from '@/lib/pr-v2/utils/as-record';

export const COMMENT_ALIAS_PREFIX = {
  server: 'server:',
  node: 'node:',
  view: 'view:',
  transition: 'transition:',
  lookup: 'lookup:',
} as const;

export const THREAD_ALIAS_PREFIX = {
  thread: 'thread:',
  node: 'node:',
} as const;

export function unique(values: Iterable<string | null | undefined>): string[] {
  const result: string[] = [];
  const seen = new Set<string>();

  for (const value of values) {
    if (typeof value !== 'string' || value.length === 0 || seen.has(value)) {
      continue;
    }

    seen.add(value);
    result.push(value);
  }

  return result;
}

export function isNumericId(value: string | null | undefined): boolean {
  return typeof value === 'string' && /^\d+$/.test(value);
}

export function getCommentLookupIds(comment: PrV2CommentResponse): string[] {
  const raw = asRecord(comment.raw);
  const nodeId = typeof raw?.node_id === 'string' ? raw.node_id : null;
  const transitionKey = getPrV2CommentTransitionKey(comment);
  const stableViewKey = getStableViewKey(comment.githubCommentId);

  return unique([
    comment.githubCommentId,
    comment.id,
    nodeId,
    transitionKey,
    stableViewKey !== comment.githubCommentId ? stableViewKey : null,
  ]);
}

export function getCommentAliasKeys(comment: PrV2CommentResponse): string[] {
  const raw = asRecord(comment.raw);
  const nodeId = typeof raw?.node_id === 'string' ? raw.node_id : null;
  const transitionKey = getPrV2CommentTransitionKey(comment);
  const stableViewKey = getStableViewKey(comment.githubCommentId);

  // Reconcile onto the strongest identifiers first so one logical comment can survive
  // queued -> submitting -> persisted even when different snapshots disagree about transport ids.
  return unique([
    isNumericId(comment.githubCommentId)
      ? `${COMMENT_ALIAS_PREFIX.server}${comment.githubCommentId}`
      : null,
    nodeId ? `${COMMENT_ALIAS_PREFIX.node}${nodeId}` : null,
    stableViewKey !== comment.githubCommentId
      ? `${COMMENT_ALIAS_PREFIX.view}${stableViewKey}`
      : null,
    transitionKey ? `${COMMENT_ALIAS_PREFIX.transition}${transitionKey}` : null,
    `${COMMENT_ALIAS_PREFIX.lookup}${comment.githubCommentId}`,
    `${COMMENT_ALIAS_PREFIX.lookup}${comment.id}`,
  ]);
}

export function getReviewLookupIds(review: PrV2ReviewResponse): string[] {
  return unique([review.githubReviewId, review.id, review.githubReviewNodeId]);
}

export function getReviewAliasKeys(review: PrV2ReviewResponse): string[] {
  return unique([
    isNumericId(review.githubReviewId)
      ? `${COMMENT_ALIAS_PREFIX.server}${review.githubReviewId}`
      : null,
    review.githubReviewNodeId ? `${COMMENT_ALIAS_PREFIX.node}${review.githubReviewNodeId}` : null,
    `${COMMENT_ALIAS_PREFIX.lookup}${review.githubReviewId}`,
    `${COMMENT_ALIAS_PREFIX.lookup}${review.id}`,
  ]);
}

export function getThreadLookupIds(thread: PrV2ReviewThread): string[] {
  return unique([thread.id, ...thread.comments.nodes.map((node) => node.id)]);
}

export function getThreadAliasKeys(thread: PrV2ReviewThread): string[] {
  return unique([
    `${THREAD_ALIAS_PREFIX.thread}${thread.id}`,
    ...thread.comments.nodes.map((node) => `${THREAD_ALIAS_PREFIX.node}${node.id}`),
  ]);
}

export function mergeLookupIds(
  previousLookupIds: string[],
  nextLookupIds: string[],
  previousComment: PrV2CommentResponse,
  nextComment: PrV2CommentResponse,
): string[] {
  return unique([
    ...previousLookupIds,
    ...nextLookupIds,
    previousComment.githubCommentId,
    nextComment.githubCommentId,
  ]);
}

export function findCommentClientId(
  scope: PrV2LocalScopeState,
  comment: PrV2CommentResponse,
): string | null {
  for (const aliasKey of getCommentAliasKeys(comment)) {
    const existingClientId = scope.commentAliasToClientId[aliasKey];
    if (existingClientId) {
      return existingClientId;
    }
  }

  return null;
}

export function findReviewClientId(
  scope: PrV2LocalScopeState,
  review: PrV2ReviewResponse,
): string | null {
  for (const aliasKey of getReviewAliasKeys(review)) {
    const existingClientId = scope.reviewAliasToClientId[aliasKey];
    if (existingClientId) {
      return existingClientId;
    }
  }

  return null;
}

export function findThreadClientId(
  scope: PrV2LocalScopeState,
  thread: PrV2ReviewThread,
): string | null {
  for (const aliasKey of getThreadAliasKeys(thread)) {
    const existingClientId = scope.threadAliasToClientId[aliasKey];
    if (existingClientId) {
      return existingClientId;
    }
  }

  return null;
}
